// FeedFilterPanelV2.jsx
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { LIVESTREAM_FEED_SORTINGS_V2 } from '../RemoteConfigKeys.js';

import ModalView from '../container/ModalView.js';

import withRouter from '../component/WithRouter.jsx';
import FeedSorterTabs from '../component/FeedSorterTabs.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';

import {
  EMPTY_OBJECT,
  EMPTY_ARRAY,
  NULL_FUNCTION,
} from '../resource/defaults.js';
import { ButtonId, ModalId } from '../resource/trackingConstants.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { isDefaultSorting } from '../resource/feedUtils.js';

import media, { hover } from '../style/media.js';
import { color, textColor, breakpoint } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';

import FilterIconSource from '../../img/ic_filter.svg';
import FilterIconBlueSource from '../../img/ic_filter_blue.svg';
import ArrowRightWhiteSource from '../../img/ic_arrow_right_white.svg';

import { removeLangFromPath } from '../resource/langPath.js';

const MODAL_VIEW_STYLE = {
  position: 'static',
};

export const FeedFilterPanelV2 = ({
  location = EMPTY_OBJECT,
  baseFeedName = '',
  sortOptions = EMPTY_ARRAY,
  filterShortcutOptions = EMPTY_ARRAY,
  selectedOptions = EMPTY_ARRAY,
  deviceWidth = 0,
  isFeedFilterModalOpened = false,
  setSelectedOption = NULL_FUNCTION,
  resetSelectedOptions = NULL_FUNCTION,
  openFeedFilterModal = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const scrollableOptionsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const isMobile = deviceWidth <= breakpoint.mobile;
  const cleanPathname = removeLangFromPath(location.pathname);
  const showSortOptions = cleanPathname === '/livestream';

  // Limit sort options to maximum 4 items
  const slicedSortOptions = useMemo(() => {
    return sortOptions?.slice(0, 4);
  }, [sortOptions]);

  // Limit filter shortcut options to maximum 10 items
  const slicedFilterShortcutOptions = useMemo(() => {
    return filterShortcutOptions?.slice(0, 10);
  }, [filterShortcutOptions]);

  const { hasNonSortingOptions, hasSortingOptions } = useMemo(() => {
    const sortingOptions = selectedOptions.filter(selectedOption =>
      selectedOption.startsWith(LIVESTREAM_FEED_SORTINGS_V2)
    );

    return {
      hasNonSortingOptions: selectedOptions.length > sortingOptions.length,
      hasSortingOptions: sortingOptions.length > 0,
    };
  }, [selectedOptions]);

  const isShowAllButtonActive = !hasNonSortingOptions;

  const handleToLeftestButtonClick = () => {
    if (scrollableOptionsRef.current) {
      scrollableOptionsRef.current.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleToRightestButtonClick = () => {
    if (scrollableOptionsRef.current) {
      scrollableOptionsRef.current.scrollTo({
        left: scrollableOptionsRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  };

  const checkScrollPosition = useCallback(() => {
    if (scrollableOptionsRef.current) {
      const { scrollLeft } = scrollableOptionsRef.current;
      const buffer = 1;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(
        scrollLeft <
          scrollableOptionsRef.current.scrollWidth -
            scrollableOptionsRef.current.clientWidth -
            buffer
      );
    }
  }, []);

  // Add scroll, resize event listener
  useEffect(() => {
    const scrollableElement = scrollableOptionsRef.current;

    if (scrollableElement) {
      checkScrollPosition();

      scrollableElement.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);

      return () => {
        scrollableElement.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [checkScrollPosition]);

  return (
    <>
      {showSortOptions && (
        <FeedSorterTabs
          baseFeedName={baseFeedName}
          sortOptions={slicedSortOptions}
          selectedOptions={selectedOptions}
          setSelectedOption={setSelectedOption}
        />
      )}
      <Container>
        <ScrollableOptions ref={scrollableOptionsRef}>
          <LeftOverlaySection show={canScrollLeft}>
            {/* Show in Mobile */}
            <GradientShadow />

            {/* Show in Desktop */}
            <ToLeftestButtonWrapper>
              <ToLeftestButton onClick={handleToLeftestButtonClick}>
                <ArrowIcon
                  src={ArrowRightWhiteSource}
                  alt="arrow left icon"
                  style={{ transform: 'rotate(180deg)' }}
                />
              </ToLeftestButton>
            </ToLeftestButtonWrapper>
          </LeftOverlaySection>

          {/* Sorts */}
          {showSortOptions && (
            <DisplayInDesktop>
              {slicedSortOptions.map(option => {
                const groupOption = `${LIVESTREAM_FEED_SORTINGS_V2}/${option}`;
                const isDefaultOption =
                  !hasSortingOptions &&
                  isDefaultSorting({ baseFeedName, option });
                const isActive =
                  selectedOptions.includes(groupOption) || isDefaultOption;

                return (
                  <MultipleFilter
                    key={option}
                    active={isActive}
                    disable={isActive}
                    data-element_id={ButtonId.All.ButtonSorting}
                    data-element_group={LIVESTREAM_FEED_SORTINGS_V2}
                    data-element_option={option}
                    data-tracking_payload={{
                      'filter.group': LIVESTREAM_FEED_SORTINGS_V2,
                      'filter.option': option,
                      'filter.selectedOptions': selectedOptions.join(','),
                    }}
                    onClick={() =>
                      setSelectedOption({
                        groupOption,
                        isSingleSelection: true,
                      })
                    }
                  >
                    {t(
                      getTranslationKey({
                        key: option,
                      }),
                      {
                        ns: TranslationNamespace.FEED,
                      }
                    )}
                  </MultipleFilter>
                );
              })}
              <Divider />
            </DisplayInDesktop>
          )}

          {/* Filters */}
          <FilterOptionList>
            <ShowAllButton
              active={isShowAllButtonActive}
              disable={isShowAllButtonActive}
              data-element_id={ButtonId.All.ButtonFilterClear}
              onClick={resetSelectedOptions}
            >
              {t('all', {
                ns: TranslationNamespace.GENERAL,
              })}
            </ShowAllButton>
            {slicedFilterShortcutOptions.map(groupOption => {
              const [group, option] = groupOption.split('/');

              return (
                <MultipleFilter
                  key={option}
                  active={selectedOptions.includes(groupOption)}
                  data-element_id={ButtonId.All.ButtonFilter}
                  data-element_group={group}
                  data-element_option={option}
                  data-tracking_payload={{
                    'filter.group': group,
                    'filter.option': option,
                    'filter.selectedOptions': selectedOptions.join(','),
                  }}
                  onClick={() => setSelectedOption({ groupOption })}
                >
                  {t(
                    getTranslationKey({
                      key: option,
                    }),
                    {
                      ns: TranslationNamespace.FEED,
                    }
                  )}
                </MultipleFilter>
              );
            })}
          </FilterOptionList>

          <RightOverlaySection show={canScrollRight}>
            {/* Show in Mobile */}
            <GradientShadow
              style={{
                left: 'auto',
                right: '-1px',
                transform: 'rotate(180deg)',
              }}
            />

            {/* Show in Desktop */}
            <ToRightestButtonWrapper>
              <ToRightestButton onClick={handleToRightestButtonClick}>
                <ArrowIcon src={ArrowRightWhiteSource} alt="arrow right icon" />
              </ToRightestButton>
            </ToRightestButtonWrapper>
          </RightOverlaySection>
        </ScrollableOptions>

        <FilterModalButton
          id="filter-modal-button"
          data-element_id={ButtonId.All.ButtonFilterDetail}
          active={isFeedFilterModalOpened}
          onClick={openFeedFilterModal}
        >
          <ButtonText>
            {t('general_filter', {
              ns: TranslationNamespace.FEED,
            })}
          </ButtonText>
          <FilterIcon
            src={
              isFeedFilterModalOpened ? FilterIconBlueSource : FilterIconSource
            }
            alt="filter icon"
          />
          <ModalView
            id="FeedFilterModal"
            noScrollLock={!isMobile}
            rootId={isMobile ? undefined : '#filter-modal-button'}
            modalViewStyle={isMobile ? undefined : MODAL_VIEW_STYLE}
            modalId={ModalId.Feed.FilterDetail}
            render={() => (
              <UniversalComponent
                filename="Container_FeedFilterModal"
                baseFeedName={baseFeedName}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/FeedFilterModal.js')
                }
              />
            )}
          />
        </FilterModalButton>
      </Container>
    </>
  );
};

FeedFilterPanelV2.propTypes = {
  location: PropTypes.object,
  baseFeedName: PropTypes.string,
  sortOptions: PropTypes.array,
  filterShortcutOptions: PropTypes.array,
  selectedOptions: PropTypes.array,
  deviceWidth: PropTypes.number,
  isFeedFilterModalOpened: PropTypes.bool,
  setSelectedOption: PropTypes.func,
  resetSelectedOptions: PropTypes.func,
  openFeedFilterModal: PropTypes.func,
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 52px;
  padding: 8px 16px;

  ${media.mobile`
    padding: 4px 16px;
    gap: 8px;
  `}
`;

const ScrollableOptions = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  overflow-x: auto;
  white-space: nowrap;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const LeftOverlaySection = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  height: 28px;
  opacity: 0;
  pointer-events: none;
  transition: 0.3s;

  ${({ show }) =>
    show &&
    `
    opacity: 1;
    pointer-events: auto;
  `}
`;

const GradientShadow = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: -1px;
  width: 20px;
  height: 28px;
  background: linear-gradient(to right, ${color.black}, transparent);
  pointer-events: none;

  ${media.mobile`
    display: block;
  `}
`;

const ToLeftestButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 28px;
  background: linear-gradient(to right, ${color.black} 60%, transparent 100%);

  ${media.mobile`
    display: none;
  `}
`;

const RightOverlaySection = styled.div`
  position: sticky;
  top: 0;
  right: 0;
  height: 28px;
  opacity: 0;
  pointer-events: none;
  transition: 0.3s;

  ${({ show }) =>
    show &&
    `
    opacity: 1;
    pointer-events: auto;
  `}
`;

const ToRightestButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 28px;
  background: linear-gradient(to left, ${color.black} 60%, transparent 100%);

  ${media.mobile`
    display: none;
  `}
`;

const ArrowIcon = styled.img`
  width: 20px;
  height: 20px;
  transition: 0.3s;
  pointer-events: none;
`;

const ToLeftestButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${color.neutral['20']};
  background: ${color.neutral['10']};

  ${hover`
    ${ArrowIcon} {
      filter: brightness(0.8);
    }
  `}

  &:active {
    ${ArrowIcon} {
      filter: brightness(0.6);
    }
  }
`;

const ToRightestButton = styled(ToLeftestButton)`
  left: auto;
  right: 0;
`;

const DisplayInDesktop = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;

  ${media.mobile`
    display: none;
  `}
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  margin: 0 12px 0 4px;
  background: ${color.neutral['100_alpha_40']};
`;

const FilterOptionList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.mobile`
    gap: 4px;
  `}
`;

const BaseOptionButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 28px;
  padding: 4px 12px;
  background: ${color.neutral['85_alpha_20']};
  border-radius: 40px;
  font-size: 14px;
  line-height: 1.5;
  color: ${color.neutral[100]};
  transition: 0.3s;

  ${hover`
    color: ${color.neutral['100_alpha_20']};
  `}

  &:active {
    background: ${color.tealBlue_alpha_20};
    color: ${color.tealBlue};
  }

  ${({ active }) =>
    active &&
    `
    background: ${color.tealBlue}33;
    color: ${color.tealBlue};

    &:active {
      background: ${color.neutral['85_alpha_20']};
      color: ${color.neutral[100]};
    }
  `}

  ${({ disable }) =>
    disable &&
    `
    pointer-events: none;
  `}

  ${media.mobile`
    height: 24px;
    padding: 2px 8px;
    font-size: 12px;
  `}
`;

const MultipleFilter = styled(BaseOptionButton)``;
const ShowAllButton = styled(BaseOptionButton)``;

const ButtonText = styled.div`
  ${TextEllipsis};
  font-size: 14px;
  line-height: 1.5;
  color: ${textColor.white};
  transition: 0.3s;

  ${media.mobile`
    font-size: 12px;
  `}
`;

const FilterIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 4px;
  transition: 0.3s;

  ${media.mobile`
    width: 14px;
    height: 14px;
  `}
`;

const FilterModalButton = styled.button`
  ${ResetButtonStyle};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  padding: 2px 8px;
  border: 1px solid #fff;
  border-radius: 4px;
  transition: 0.3s;

  &:active {
    border-color: ${color.sky};
    background: ${color.sky_alpha_40};

    ${ButtonText} {
      color: ${color.sky};
    }
  }

  ${({ active }) =>
    active &&
    `
    border-color: ${color.sky};
    background: ${color.sky_alpha_40};
    pointer-events: none;

    ${ButtonText} {
      color: ${color.sky};
    }
  `}
`;

export default withRouter(FeedFilterPanelV2);
